import { useQuery } from '@apollo/client/react';
import { useState } from 'react';
import { ArrowClockwise, Download } from 'react-bootstrap-icons';

import {
  BillingHistoryDocument,
  type BillingHistoryQuery,
  type BillingHistoryQueryVariables,
  BillingHistoryStatus,
  DataTestIds,
} from '@repo/frontend-shared';
import { SDSButton } from '@repo/sds/SDSButton';
import { SDSFlexbox } from '@repo/sds/SDSFlexbox';
import { SDSIconButton } from '@repo/sds/SDSIconButton';
import { SDSPagination } from '@repo/sds/SDSPagination';
import { SDSTable, type TableColumn } from '@repo/sds/SDSTable';
import { SDSText } from '@repo/sds/SDSText';
import { SettingsCard } from '@repo/ui/components/elements/settings-card/settings-card';

import { formatBillingDate } from './billing-format';
import {
  buildBillingHistoryAmountLines,
  describeBillingHistoryCommitment,
  describeBillingHistoryInvoice,
  describeBillingHistoryStatus,
} from './billing-history-format';
import { BILLING_HISTORY_PAGE_SIZE, BILLING_HISTORY_PAGE_SIZES, pruneStaleCursors } from './billing-history-pagination';

type BillingHistoryEntry = BillingHistoryQuery['myBillingHistory']['edges'][number]['node'];

/** The subset of SDS colours the status text accepts. */
type BillingStatusTone = 'primary' | 'alert' | 'inactive';

/**
 * Settled money reads in the brand colour, money that moved back or never
 * landed reads as an error, and a state still in flight stays inactive.
 */
const STATUS_TONES: Record<BillingHistoryStatus, BillingStatusTone> = {
  [BillingHistoryStatus.PENDING]: 'inactive',
  [BillingHistoryStatus.SUCCEEDED]: 'primary',
  [BillingHistoryStatus.FAILED]: 'alert',
  [BillingHistoryStatus.CANCELLED]: 'inactive',
  [BillingHistoryStatus.PARTIALLY_REFUNDED]: 'alert',
  [BillingHistoryStatus.REFUNDED]: 'alert',
  [BillingHistoryStatus.DISPUTED]: 'alert',
  [BillingHistoryStatus.SUPPORT_REQUIRED]: 'alert',
};

function StatusCell({ entry }: { entry: BillingHistoryEntry }) {
  return (
    <SDSText
      type="body-medium"
      text={describeBillingHistoryStatus(entry.status)}
      // A state this build does not know reads neutral rather than alarming;
      // the label already says what it is.
      color={Object.hasOwn(STATUS_TONES, entry.status) ? STATUS_TONES[entry.status] : 'inactive'}
    />
  );
}

function DescriptionCell({ entry }: { entry: BillingHistoryEntry }) {
  // The commitment leads: it is what the design puts under the name, and it
  // is the line a customer scanning for "which of my six payments is this"
  // reads. Invoice evidence follows rather than replacing it — the number is
  // what support asks for, and an instalment row carries both.
  const commitment = describeBillingHistoryCommitment(entry);
  const invoiceLine = describeBillingHistoryInvoice(entry.invoice);
  return (
    <SDSFlexbox direction="column" gap="text-small" alignItems="flex-start">
      <SDSText type="body-medium" text={entry.description} />
      {commitment ? <SDSText type="body-small" text={commitment} color="inactive" /> : null}
      {invoiceLine ? <SDSText type="body-small" text={invoiceLine} color="inactive" /> : null}
      {/* The date, for the narrow layout that drops its column. Rendered once
          and hidden by width rather than swapped in by a breakpoint hook, so
          the table has one markup shape and no resize re-render. */}
      <SDSText
        className="BillingHistory__stacked"
        type="body-small"
        text={formatBillingDate(entry.occurredAt)}
        color="inactive"
      />
    </SDSFlexbox>
  );
}

function AmountCell({ entry }: { entry: BillingHistoryEntry }) {
  const amounts = buildBillingHistoryAmountLines(entry);
  return (
    <SDSFlexbox direction="column" gap="text-small" alignItems="flex-end">
      <SDSText type="body-medium" text={amounts.gross} />
      {amounts.refunded ? <SDSText type="body-small" text={amounts.refunded} color="inactive" /> : null}
      {amounts.net ? <SDSText type="body-small" text={amounts.net} color="inactive" /> : null}
      {/* The status, for the narrow layout that drops its column. */}
      <SDSText
        className="BillingHistory__stacked"
        type="body-small"
        text={describeBillingHistoryStatus(entry.status)}
        color={Object.hasOwn(STATUS_TONES, entry.status) ? STATUS_TONES[entry.status] : 'inactive'}
      />
    </SDSFlexbox>
  );
}

const COLUMNS: TableColumn<BillingHistoryEntry>[] = [
  {
    id: 'description',
    label: 'Purchase details',
    cellDataTestId: DataTestIds.BILLING.HISTORY_CELL_DESCRIPTION,
    render: entry => <DescriptionCell entry={entry} />,
  },
  {
    id: 'occurredAt',
    label: 'Date',
    width: '158px',
    cellDataTestId: DataTestIds.BILLING.HISTORY_CELL_DATE,
    render: entry => <SDSText type="body-medium" text={formatBillingDate(entry.occurredAt)} />,
  },
  {
    id: 'amount',
    label: 'Amount',
    align: 'right',
    width: '125px',
    cellDataTestId: DataTestIds.BILLING.HISTORY_CELL_AMOUNT,
    render: entry => <AmountCell entry={entry} />,
  },
  {
    id: 'status',
    label: 'Status',
    align: 'left',
    width: '100px',
    cellDataTestId: DataTestIds.BILLING.HISTORY_CELL_STATUS,
    render: entry => <StatusCell entry={entry} />,
  },
];

function readHistoryPageInfo(
  data: BillingHistoryQuery | undefined,
): BillingHistoryQuery['myBillingHistory']['pageInfo'] {
  return data?.myBillingHistory.pageInfo ?? { __typename: 'PageInfo', endCursor: null, hasNextPage: false };
}

function useBillingHistory() {
  const [pageSize, setPageSize] = useState(BILLING_HISTORY_PAGE_SIZE);
  // `cursors[n]` is the cursor that opens page n. The first page opens with
  // none, and each page learns the next one's cursor only by being read — a
  // keyset has no way to address a page it has never reached.
  const [cursors, setCursors] = useState<(string | undefined)[]>([undefined]);
  const [pageIndex, setPageIndex] = useState(0);
  const historyQuery = useQuery<BillingHistoryQuery, BillingHistoryQueryVariables>(BillingHistoryDocument, {
    variables: { first: pageSize, after: cursors[pageIndex] },
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
    // The app polls every watched query hourly by default. A poll re-runs the
    // page and replaces the field, so a reader sitting on page four would
    // watch it reload underneath them on a timer.
    pollInterval: 0,
  });
  const [isReloading, setIsReloading] = useState(false);
  const [reloadFailed, setReloadFailed] = useState(false);
  // The page being replaced stays on screen while the next one is in flight.
  // Apollo empties `data` the moment the variables change, and without this
  // the table, the total and the controls all vanish on every click — the
  // reader loses their place in the middle of navigating.
  //
  // Keep the last successful page visible both while the next request is in
  // flight and if that request fails. The failure state disables pagination,
  // so these rows are display-only and cannot be mistaken for a fetched page.
  const shownData = historyQuery.data ?? historyQuery.previousData;
  const { endCursor, hasNextPage } = readHistoryPageInfo(shownData);
  const entries = shownData?.myBillingHistory.edges.map(edge => edge.node) ?? [];
  const totalCount = shownData?.myBillingHistory.totalCount ?? undefined;

  /**
   * Pages the reader can actually open.
   *
   * The total says how many exist; the cursors say which are addressable. A
   * page beyond the last one visited has no cursor, so it is counted but not
   * offered — walking forward is what discovers it.
   */
  // Only the last page visited holds a cursor for the one after it. Standing
  // anywhere earlier, the frontier is still where it was — reading page one
  // again does not make page four addressable.
  const isOnLastVisitedPage = pageIndex === cursors.length - 1;
  // `endCursor` is required here as well as in `goToPage`: offering a page on
  // `hasNextPage` alone, while only navigating when a cursor exists, renders
  // an enabled control whose click does nothing.
  const canAdvance = isOnLastVisitedPage && hasNextPage && Boolean(endCursor);
  const knownPageCount = cursors.length + (canAdvance ? 1 : 0);
  // Never fewer pages than can actually be reached. The total and the page
  // size are two independent facts about the server, and a page that comes
  // back shorter than asked — or a total counted a moment earlier — would
  // otherwise say "one page" while `hasNextPage` is still offering another,
  // stranding the reader on the first.
  const countedPageCount = totalCount === undefined ? 0 : Math.ceil(totalCount / pageSize);
  const pageCount = Math.max(1, countedPageCount, knownPageCount);
  const reachablePageCount = Math.min(pageCount, knownPageCount);

  // Pruning must only compare against an `endCursor` that is actually the
  // current `pageIndex`'s own — never the outgoing page's `previousData`
  // carried above for display continuity. While a jump is in flight,
  // `historyQuery.data` is empty (see the comment above `shownData`), so
  // pruning simply waits; using the stale `endCursor` here instead would
  // read it as a moved boundary for a page it was never fetched for, and
  // wrongly discard cursors for pages that are still perfectly valid — most
  // reachable by jumping more than one page via the pagination number
  // buttons, e.g. page 4 straight to page 1.
  const freshEndCursor = historyQuery.data ? endCursor : undefined;

  // `pruneStaleCursors` is pure and cheap, so the desired cursor list is
  // adjusted during render (see https://react.dev/learn/you-might-not-need-an-effect)
  // instead of in an effect: this corrects `cursors` before the render
  // commits, rather than one render later, and — since it compares against
  // the current `cursors` value directly instead of a snapshot of the last
  // inputs it ran for — only calls `setCursors` when pruning would actually
  // change something, so a normal page turn costs no extra render.
  const prunedCursors = pruneStaleCursors(cursors, pageIndex, freshEndCursor);
  if (prunedCursors.length !== cursors.length) {
    setCursors(prunedCursors);
  }

  const goToPage = (nextIndex: number): void => {
    if (nextIndex === pageIndex || nextIndex < 0 || historyQuery.loading || isReloading) {
      return;
    }
    setReloadFailed(false);
    // One step past the last visited page is reachable because that page just
    // handed us its cursor. The guard has to include where the reader is
    // standing: from an earlier page the cursor in hand opens the page after
    // *that* one, and filing it under the frontier would send them somewhere
    // they have already been and lose every row beyond it.
    if (nextIndex === cursors.length && canAdvance && endCursor) {
      setCursors(previous => [...previous, endCursor]);
      setPageIndex(nextIndex);
      return;
    }
    if (nextIndex < cursors.length) {
      setPageIndex(nextIndex);
    }
  };

  const changePageSize = (nextSize: number): void => {
    if (nextSize === pageSize) {
      return;
    }
    // Every cursor was cut against the old page size, so none of them lands on
    // a boundary the new one shares. Starting over is the only honest reset.
    setReloadFailed(false);
    setPageSize(nextSize);
    setCursors([undefined]);
    setPageIndex(0);
  };

  /**
   * Reads the current page again.
   *
   * The cursor that opened it still names the same boundary, so a refresh
   * stays where the reader is rather than sending them back to the newest
   * page.
   */
  const reload = async (): Promise<void> => {
    if (isReloading) {
      return;
    }
    setIsReloading(true);
    setReloadFailed(false);
    try {
      await historyQuery.refetch();
    } catch {
      setReloadFailed(true);
    }
    setIsReloading(false);
  };

  return {
    entries,
    totalCount,
    pageSize,
    pageIndex,
    pageCount,
    reachablePageCount,
    hasNextPage,
    goToPage,
    changePageSize,
    reload,
    isReloading,
    isPaging: historyQuery.loading,
    // `cache-and-network` reports loading on every refetch. Only a request with
    // nothing to show yet is a loading state; one that already has rows is a
    // refresh, and blanking the table for it loses the reader's place.
    isInitialLoading: historyQuery.loading && entries.length === 0,
    hasError: Boolean(historyQuery.error) || reloadFailed,
  };
}

type BillingHistoryState = ReturnType<typeof useBillingHistory>;

function HistoryPlaceholder({ dataTestId, text }: { dataTestId: string; text: string }) {
  return <SDSText type="body-medium" text={text} color="text-secondary" dataTestId={dataTestId} />;
}

function ReloadButton({
  dataTestId,
  isReloading,
  onReload,
}: {
  dataTestId: string;
  isReloading: boolean;
  onReload: () => void;
}) {
  return (
    <SDSButton
      text={isReloading ? 'Reloading…' : 'Reload'}
      mode="outline"
      size="small"
      leftIcon={<ArrowClockwise />}
      isDisabled={isReloading}
      ariaBusy={isReloading}
      onClick={onReload}
      dataTestId={dataTestId}
    />
  );
}

/**
 * The banner over a table whose rows are older than the last request.
 *
 * A refresh that fails leaves rows on screen that are still true but no longer
 * current. Silently keeping them says the opposite, and clearing them throws
 * away a history the customer can still read.
 */
function StaleWarning({ isReloading, onReload }: { isReloading: boolean; onReload: () => void }) {
  return (
    <SDSFlexbox
      direction="row"
      gap="text-small"
      alignItems="center"
      wrap="wrap"
      dataTestId={DataTestIds.BILLING.HISTORY_STALE_WARNING}
    >
      <SDSText type="body-small" text="This history could not be refreshed, so it may be out of date." color="alert" />
      <ReloadButton dataTestId={DataTestIds.BILLING.HISTORY_RETRY} isReloading={isReloading} onReload={onReload} />
    </SDSFlexbox>
  );
}

function HistoryPagination({ history }: { history: BillingHistoryState }) {
  // No guard on an empty first page: every call site has already returned the
  // empty or error state for that, so a condition here would be unreachable.
  // The bar stays for a single page — "Total: 2" is worth saying even when
  // there is nowhere to page to.

  return (
    <SDSPagination
      pageIndex={history.pageIndex}
      pageCount={history.pageCount}
      reachablePageCount={history.reachablePageCount}
      totalCount={history.totalCount}
      pageSize={history.pageSize}
      pageSizeOptions={BILLING_HISTORY_PAGE_SIZES}
      onChangePageSize={history.changePageSize}
      isDisabled={history.isPaging || history.isReloading}
      onGoToPage={history.goToPage}
      dataTestId={DataTestIds.BILLING.HISTORY_PAGINATION}
    />
  );
}

function RowAction({
  entry,
  isPortalLoading,
  isReceiptLoading,
  onViewReceipt,
}: BillingHistorySectionProps & { entry: BillingHistoryEntry }) {
  // Provider evidence is a payment reference, which is not the same as an
  // invoice. Only a row that has one may say so; the rest offer the receipt
  // the payment itself has.
  const label = entry.invoice ? 'View invoice' : 'View receipt';

  return (
    <SDSIconButton
      icon={<Download />}
      isDisabled={isPortalLoading || isReceiptLoading}
      // The icon carries no words, so the accessible name is the only thing
      // naming both the action and the row it belongs to.
      ariaLabel={`${label} for ${entry.description}`}
      onClick={() => {
        onViewReceipt(entry.id);
      }}
      dataTestId={DataTestIds.BILLING.HISTORY_ROW_ACTION}
    />
  );
}

function HistoryTable({
  history,
  isPortalLoading,
  isReceiptLoading,
  onViewReceipt,
}: BillingHistorySectionProps & { history: BillingHistoryState }) {
  return (
    <SDSFlexbox direction="column" gap="text-medium" fullWidth>
      {/* One failure, one message. A failed next page already says so under
          the table; adding "this may be out of date" over it would be a second
          and different claim about the same request. */}
      {history.hasError ? (
        <StaleWarning isReloading={history.isReloading} onReload={() => void history.reload()} />
      ) : null}
      <SDSTable<BillingHistoryEntry>
        // This table used to be remounted on a key when the Stripe-customer
        // lookup resolved, because the action column was gated on it and the
        // first renderer stuck. The action now reads the row's own
        // `hasProviderEvidence`, which arrives with the row, so there is no
        // later value for a stale renderer to miss and nothing to remount for.
        className="BillingHistory__table"
        dataTestId={DataTestIds.BILLING.HISTORY_TABLE}
        columns={COLUMNS}
        data={history.entries}
        // The server orders the history; sorting one loaded page client-side
        // would only reorder that page against the ones behind it.
        sortable={false}
        renderActions={(entry: BillingHistoryEntry) =>
          // Evidence at the provider is the whole condition: a receipt is
          // fetched for this row's own payment, so it does not depend on the
          // account resolving a Stripe customer the way the portal did. A row
          // we only know about locally still has nothing to show.
          entry.hasProviderEvidence ? (
            <RowAction
              entry={entry}
              isPortalLoading={isPortalLoading}
              isReceiptLoading={isReceiptLoading}
              onViewReceipt={onViewReceipt}
            />
          ) : (
            <SDSText type="body-small" text="—" color="text-secondary" />
          )
        }
        actionsLabel="Action"
      />
      <HistoryPagination history={history} />
    </SDSFlexbox>
  );
}

interface BillingHistorySectionProps {
  isPortalLoading: boolean;
  isReceiptLoading: boolean;
  onViewReceipt: (entryId: string) => void;
}

/**
 * The account's billing history: one row per thing bought, plus any attempt
 * that never became one.
 *
 * It replaces the separate attempt and purchase lists, which showed a settled
 * charge twice and never said which of the two was the record.
 */
export function BillingHistorySection(props: BillingHistorySectionProps) {
  const history = useBillingHistory();

  return (
    <SettingsCard className="BillingHistory" direction="column" dataTestId={DataTestIds.BILLING.HISTORY_SECTION}>
      <SDSFlexbox direction="column" gap="text-medium" fullWidth>
        <SDSText text="Billing History" type="h5" />
        <BillingHistoryContent history={history} {...props} />
      </SDSFlexbox>
    </SettingsCard>
  );
}

function BillingHistoryContent({ history, ...portal }: BillingHistorySectionProps & { history: BillingHistoryState }) {
  if (history.isInitialLoading) {
    return (
      <HistoryPlaceholder dataTestId={DataTestIds.BILLING.HISTORY_LOADING_STATE} text="Loading your billing history…" />
    );
  }

  // Error before not-found: a request that failed has not established that
  // there is nothing to show, and "no billing activity yet" is a claim about
  // the account rather than about the request.
  if (history.hasError && history.entries.length === 0) {
    return (
      <SDSFlexbox direction="column" gap="text-x-large" alignItems="flex-start" fullWidth>
        <HistoryPlaceholder
          dataTestId={DataTestIds.BILLING.HISTORY_ERROR_STATE}
          text={
            history.pageIndex > 0
              ? 'This page of your billing history could not be loaded.'
              : 'Your billing history could not be loaded right now.'
          }
        />
        <ReloadButton
          dataTestId={DataTestIds.BILLING.HISTORY_RETRY}
          isReloading={history.isReloading}
          onReload={() => {
            void history.reload();
          }}
        />
        {/* A page that failed still has pages either side of it. Without the
            controls the reader's only way back to rows they were reading is a
            full reload. */}
        {history.pageIndex > 0 ? <HistoryPagination history={history} /> : null}
      </SDSFlexbox>
    );
  }

  // An empty page reached from a cursor says nothing about the account: the
  // rows it named may have been consolidated away since. Claiming "no billing
  // activity" there is false, and dropping the controls with it leaves a
  // reload as the only way back to the rows that do exist.
  if (history.entries.length === 0 && history.pageIndex > 0) {
    return (
      <SDSFlexbox direction="column" gap="text-x-large" alignItems="flex-start" fullWidth>
        <HistoryPlaceholder
          dataTestId={DataTestIds.BILLING.HISTORY_STALE_PAGE_STATE}
          text="This page is empty — your billing history has changed since it was opened."
        />
        <HistoryPagination history={history} />
      </SDSFlexbox>
    );
  }

  if (history.entries.length === 0) {
    return (
      <HistoryPlaceholder
        dataTestId={DataTestIds.BILLING.HISTORY_EMPTY_STATE}
        text="No billing activity on this account yet."
      />
    );
  }

  return <HistoryTable history={history} {...portal} />;
}
